import { Box, type BoxProps, Text, type TextProps } from '@chakra-ui/react';
import { useMixpanel } from 'gatsby-plugin-mixpanel';
import type * as React from 'react';
import { colors, fonts } from './tokens';

/** Full-width band with a centred 1180px column. Generous vertical space
 * so each section is the only thing on screen (UX review, Sep 2026). */
export const Section: React.FC<BoxProps> = ({
  children,
  py,
  pt,
  pb,
  ...rest
}) => {
  // Resolve top and bottom separately: a responsive `py` would otherwise
  // override a plain `pt={0}` from md up (its media query wins).
  const band = py ?? { base: '80px', md: '128px' };
  return (
    <Box
      as="section"
      px={{ base: '20px', md: '28px' }}
      pt={pt ?? band}
      pb={pb ?? band}
      {...rest}
    >
      <Box maxW="1180px" mx="auto">
        {children}
      </Box>
    </Box>
  );
};

type DisplayProps = BoxProps & { as?: 'h1' | 'h2' | 'h3' | 'span' };

/** Playfair Display heading. Defaults to a section-level h2. */
export const Display: React.FC<DisplayProps> = ({
  as = 'h2',
  children,
  ...rest
}) => (
  <Box
    as={as}
    m={0}
    fontFamily={fonts.display}
    fontWeight={800}
    letterSpacing="-0.02em"
    lineHeight={1.1}
    fontSize={{ base: '32px', md: '40px', lg: '46px' }}
    {...rest}
  >
    {children}
  </Box>
);

export const Eyebrow: React.FC<TextProps> = ({ children, ...rest }) => (
  <Text m={0} fontSize="15px" fontWeight={700} color={colors.ink70} {...rest}>
    {children}
  </Text>
);

/** A quiet secondary action. It deliberately has a full touch target without
 * competing visually with a filled conversion button. */
export const TextLink: React.FC<{
  href: string;
  children: React.ReactNode;
  event?: string;
  light?: boolean;
}> = ({ href, children, event, light = false }) => {
  const mixpanel = useMixpanel();
  return (
    <Box
      as="a"
      href={href}
      onClick={event ? () => mixpanel.track(event) : undefined}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      minH="44px"
      px="4px"
      color={light ? colors.onDark : colors.blue}
      fontSize="16px"
      fontWeight={700}
      textDecoration="underline"
      textUnderlineOffset="4px"
      sx={{ textDecorationThickness: '1.5px' }}
      _hover={{ color: light ? colors.lime : colors.press }}
    >
      {children} →
    </Box>
  );
};

export const Lead: React.FC<TextProps> = ({ children, ...rest }) => (
  <Text
    m={0}
    fontSize={{ base: '16px', md: '18px' }}
    lineHeight={1.7}
    color={colors.ink70}
    {...rest}
  >
    {children}
  </Text>
);

const tones = {
  blue: { bg: colors.blue, color: colors.onDark, hover: colors.press },
  white: { bg: colors.white, color: colors.ink, hover: colors.paper },
  lime: { bg: colors.lime, color: colors.onLight, hover: '#B8ED6A' },
  ink: { bg: colors.inkBg, color: colors.onDark, hover: '#1F2536' },
} as const;

type CtaButtonProps = {
  href: string;
  children: React.ReactNode;
  tone?: keyof typeof tones;
  size?: 'sm' | 'md' | 'lg';
  shadow?: boolean;
  /** Mixpanel event name fired on click. */
  event?: string;
  /** Full width on phones, so stacked button pairs line up. Defaults on for
   * large (hero and closing) buttons. */
  block?: boolean;
};

const sizes = {
  sm: { px: '18px', py: '9px', fontSize: '15px', borderRadius: '10px' },
  md: { px: '24px', py: '13px', fontSize: '16px', borderRadius: '12px' },
  lg: { px: '30px', py: '15px', fontSize: '17px', borderRadius: '12px' },
};

/** Outlined button link. With `shadow`, it presses into its offset shadow. */
export const CtaButton: React.FC<CtaButtonProps> = ({
  href,
  children,
  tone = 'blue',
  size = 'lg',
  shadow = false,
  event,
  block,
}) => {
  const full = block ?? size === 'lg';
  const mixpanel = useMixpanel();
  const t = tones[tone];
  return (
    <Box
      as="a"
      href={href}
      onClick={event ? () => mixpanel.track(event) : undefined}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      whiteSpace="nowrap"
      w={full ? { base: '100%', sm: 'auto' } : undefined}
      bg={t.bg}
      className={tone === 'white' ? 'cy-surface' : undefined}
      color={t.color}
      border="2px solid"
      borderColor={colors.ink}
      fontWeight={700}
      lineHeight={1.2}
      boxShadow={shadow ? `5px 5px 0 ${colors.ink}` : undefined}
      transition="transform .18s, box-shadow .18s, background-color .18s"
      _hover={{
        bg: t.hover,
        color: t.color,
        textDecoration: 'none',
        // One hover language: shadowed buttons press into their shadow;
        // flat buttons just darken (no lift).
        transform: shadow ? 'translate(2px, 2px)' : undefined,
        boxShadow: shadow ? `3px 3px 0 ${colors.ink}` : undefined,
      }}
      _focusVisible={{
        outline: `3px solid ${colors.blue}`,
        outlineOffset: '3px',
      }}
      {...sizes[size]}
    >
      {children}
    </Box>
  );
};

/** Small round status tick. */
export const TickDot: React.FC<{ size?: number; bg?: string }> = ({
  size = 24,
  bg = colors.verified,
}) => (
  <Box
    as="span"
    flex={`0 0 ${size}px`}
    w={`${size}px`}
    h={`${size}px`}
    borderRadius="50%"
    bg={bg}
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
  >
    <TickIcon size={Math.round(size / 2)} />
  </Box>
);

export const TickIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 12,
  color = colors.onDark,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ stroke: color }}
    strokeWidth="3.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/** Decorative icon treatment for non-interactive capability cards. The soft
 * offset circles give simple line icons the same visual character everywhere. */
export const IconBadge: React.FC<{
  children: React.ReactNode;
  accent?: string;
  size?: string;
}> = ({ children, accent = colors.sky, size = '52px' }) => (
  <Box position="relative" w={size} h={size} flexShrink={0}>
    <Box
      position="absolute"
      inset={0}
      bg={colors.white}
      className="cy-surface"
      border="2px solid"
      borderColor={colors.ink}
      borderRadius="14px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex={1}
      sx={{ '& svg': { position: 'relative', zIndex: 1 } }}
    >
      {children}
    </Box>
    <Box
      position="absolute"
      right="-5px"
      bottom="-5px"
      w="18px"
      h="18px"
      bg={accent}
      border="2px solid"
      borderColor={colors.ink}
      borderRadius="50%"
    />
  </Box>
);

type Stat = {
  before: string;
  after: string;
  unit: string;
  source: string;
  /** Optional link to the proof, shown as a subtle text link. */
  href?: string;
  linkLabel?: string;
};

/** Before → after proof pill (approved proof points only). */
export const CalloutPill: React.FC<{ stat: Stat }> = ({ stat }) => (
  <Box
    display="inline-flex"
    alignItems="center"
    flexWrap="wrap"
    justifyContent="center"
    gap="10px"
    bg={colors.white}
    className="cy-surface"
    border="2px solid"
    borderColor={colors.ink}
    borderRadius="999px"
    boxShadow={`4px 4px 0 ${colors.ink}`}
    px="20px"
    py="9px"
    fontSize={{ base: '15px', md: '17px' }}
    fontWeight={700}
    mt="4px"
  >
    <Box
      as="s"
      color={colors.muted}
      fontWeight={600}
      sx={{ textDecorationThickness: '2px' }}
    >
      {stat.before}
    </Box>
    <Box as="span" aria-hidden="true" color={colors.ink}>
      →
    </Box>
    <Box as="span" color={colors.verifiedText} fontWeight={800}>
      {stat.after} {stat.unit}
    </Box>
    <Box as="span" color={colors.muted} fontWeight={600}>
      at {stat.source}
    </Box>
    {stat.href && (
      <Box
        as="a"
        href={stat.href}
        color={colors.blue}
        fontSize={{ base: '14px', md: '15px' }}
        fontWeight={700}
        textDecoration="underline"
        textUnderlineOffset="3px"
        sx={{ textDecorationThickness: '1.5px' }}
        _hover={{ color: colors.press }}
      >
        {stat.linkLabel ?? 'Read the case study'} →
      </Box>
    )}
  </Box>
);
